import axios, { InternalAxiosRequestConfig } from "axios";
import { Store } from "@reduxjs/toolkit";

interface CreateApiClientOptions {
  baseURL: string;
  onLogout: () => any;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

export const createApiClient = ({ baseURL, onLogout }: CreateApiClientOptions) => {
  const instance = axios.create({ baseURL, withCredentials: true });

  // Shared so a burst of 401s triggers one refresh; the backend rotates the
  // refresh token, so a second concurrent refresh would be rejected.
  let pendingRefresh: Promise<unknown> | null = null;
  const refreshSession = () => {
    pendingRefresh ??= axios
      .post(`${baseURL}/auth/refreshToken`, null, { withCredentials: true })
      .finally(() => {
        pendingRefresh = null;
      });
    return pendingRefresh;
  };

  // Remembered so a repeat call (HMR re-running store setup) ejects the exact
  // interceptor it previously registered, rather than assuming its id is 0 —
  // axios never reuses ejected ids, so a hardcoded 0 would leak a stale
  // interceptor on every reload after the first.
  let responseInterceptorId: number | null = null;

  const setupResponseInterceptors = (store: Store) => {
    if (responseInterceptorId !== null) {
      instance.interceptors.response.eject(responseInterceptorId);
    }
    responseInterceptorId = instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config as RetriableConfig | undefined;
        if (error.response?.status === 401 && config && !config._retried) {
          config._retried = true;
          // Retried even if this refresh fails: another tab may have just rotated the cookies.
          await refreshSession().catch(() => undefined);
          return instance(config);
        }
        if (error.response?.status === 401) {
          store.dispatch(onLogout());
        }
        if (error.response?.status === 429) {
          return Promise.reject({
            status: 429,
            message: error.response.data?.message || "Too many requests. Please try again later.",
          });
        }
        return Promise.reject({
          status: error.status,
          message: error.response?.data?.payload?.message || error.message,
        });
      }
    );
  };

  return { instance, setupResponseInterceptors, refreshSession };
};
